import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useCallback, useMemo, useState } from 'react';
import Avatar from 'react-avatar';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
// eslint-disable-next-line no-restricted-imports
import { Grid, Header, Modal, Segment } from 'semantic-ui-react';

import {
  AddCircleIcon,
  Button,
  ButtonLink,
  DataTable,
  DownloadIcon,
  exportDataTable,
  getInitials,
  MainContentLayout,
  ReportToolbar,
  Spinner,
  UploadIcon,
  UserGroupIcon,
  WidgetSizeEnum,
} from '@wonderschool/common-base-ui';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import wsLogo from '../../assets/images/wonderschool/logo-base.png';
import ShowSuccess from '../../Components/Messages/ShowSuccess';
import { NoResults } from '../../Components/Shared/NoResults';
import withPermission from '../../Components/Shared/withPermission';
import { getScheduledDaysAbbreviated } from '../../enrollments';
import { dateFormatter, getAgeFromBirthday, toDateObject } from '../../helpers/dates';
import { capitalize, formatFullName } from '../../helpers/utils';
import { useOrganization, useOrganizationContactsListener } from '../../hooks/useOrganizations';
import useRooms from '../../hooks/useRooms';
import { useUser } from '../../hooks/useUser';
import { RouteNameEnum, useRoutes } from '../../navigation';
import { paths } from '../../navigation/paths';
import { useAddFamiliesEnabled } from '../../Pages/AddFamilies/featureFlag';
import { studentAddCleared, studentSelected } from '../../redux/actions/studentsActions';
import { useAddedStudent, useStudents } from '../studentsHooks';
import { getEnrollmentDate, getEnrollmentStatus, isStudentArchived } from '../studentsUtils';
import { getStudentsViewState } from '../studentsViewState';
import { EnrollmentStatusPill } from './enrollment/EnrollmentStatus';
import styles from './students.module.scss';
import StudentsFilters from './StudentsFilters';

const ImportButton = () => {
  const navigate = useNavigate();
  const { gotoRouteByName } = useRoutes();
  const { t } = useTranslation();
  const isAddFamiliesEnabled = useAddFamiliesEnabled();

  if (!isAddFamiliesEnabled) {
    // Existing behavior: route to the legacy /import/students flow.
    return (
      <Button
        onClick={() => {
          gotoRouteByName(RouteNameEnum.STUDENTS_IMPORT);
        }}
        data-testid="import-students-btn"
        size={WidgetSizeEnum.SMALL}
        preIcon={<DownloadIcon className="size-5" />}
      >
        {t('students.roster.importAction')}
      </Button>
    );
  }

  return (
    <Button
      onClick={() => {
        navigate(`${paths.addFamilies}?start=upload`);
      }}
      data-testid="import-students-btn"
      size={WidgetSizeEnum.SMALL}
      preIcon={<UploadIcon className="size-5" />}
    >
      {t('students.roster.uploadListAction')}
    </Button>
  );
};

const AddStudentButton = () => {
  const { t } = useTranslation();
  const { gotoRouteByName } = useRoutes();

  return (
    <Button
      primary
      onClick={() => {
        gotoRouteByName(RouteNameEnum.NEW_STUDENT_ADD);
      }}
      data-testid="add-student-btn"
      size={WidgetSizeEnum.SMALL}
      postIcon={<AddCircleIcon className="size-5" />}
    >
      {t('students.roster.addAction')}
    </Button>
  );
};
const ImportStudentsWithPermission = withPermission(ImportButton, 'can_edit_import');
const AddStudentWithPermission = withPermission(AddStudentButton, 'can_create_student');

/**
 * Empty state shown when the org has no students yet. Because PageTitle
 * hides the header Import/Add buttons when the roster is empty, this panel
 * is the primary entry point, so it carries both actions: import a whole
 * roster (routes to the Add Families flow) or add a single student.
 *
 * @handoff Permissions: the header buttons are gated by 'can_edit_import' /
 * 'can_create_student'. Wrap these CTAs with the same checks before ship.
 */
const NoStudentsEmptyState = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { gotoRouteByName } = useRoutes();

  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white px-6 py-16 text-center" data-testid="no-students">
      {/* Mirror the billing empty state exactly: badge -> bold navy title ->
          muted subtitle -> actions, with identical text classes. */}
      <div className="flex flex-col items-center gap-4">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-900/10">
          <UserGroupIcon className="h-8 w-8 text-blue-900" />
        </span>
        <div>
          <h3 className="text-xl font-bold text-navy-950">{t('students.roster.emptyStateTitle')}</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-gray-600">{t('students.roster.emptyStateDescription')}</p>
        </div>
        <div className="mt-2 flex flex-col items-center gap-3">
          <Button
            primary
            data-testid="empty-import-roster-btn"
            preIcon={<UploadIcon className="size-5" />}
            onClick={() => navigate(`${paths.addFamilies}?start=upload`)}
          >
            {t('students.roster.uploadListAction')}
          </Button>
          <ButtonLink
            data-testid="empty-add-student-btn"
            content={t('students.roster.manualAddAction')}
            onClick={() => gotoRouteByName(RouteNameEnum.NEW_STUDENT_ADD)}
          />
        </div>
      </div>
    </div>
  );
};

const PageTitle = ({ state }) => {
  const { t } = useTranslation();
  const { list: students, count, isLoading } = useStudents(state);
  const isAddFamiliesEnabled = useAddFamiliesEnabled();

  // No students at all: the blank-state card carries the actions, so the
  // header is just the title (avoids competing top-right buttons).
  // Flag off keeps the legacy condition so the header behaves as it does today.
  const showMinimalHeader = isAddFamiliesEnabled ? isLoading || count === 0 : !students?.length && !state.filters;
  if (showMinimalHeader) {
    return (
      <Segment basic clearing>
        <Header as="h1" floated="left" content={t('students.roster.title')} data-testid="students-title" />
      </Segment>
    );
  }

  return (
    <div className="mb-4">
      <div className={styles.studentPageTitleWrap}>
        <div>
          <h1 className="text-3xl font-bold" data-testid="students-header">
            {t('students.roster.title')}
          </h1>
        </div>

        <div className="flex w-full flex-col gap-2 pt-2 sm:flex-row md:w-auto">
          <ImportStudentsWithPermission />
          <AddStudentWithPermission />
        </div>
      </div>
    </div>
  );
};

const ShowRecordsCount = ({ state }) => {
  const { t } = useTranslation();
  return (
    <>
      {!(state?.activeTab === 'all') ? (
        <div className={styles.showCount} data-testid="count-wrapper">
          {`${t('students.roster.paginationShowing')}
          ${t(state?.count?.[state?.activeTab])}
          ${t('students.roster.paginationOf')} ${state?.count.all} ${t(capitalize(state?.activeTab)).toLowerCase()}
           ${t('students.roster.title').toLowerCase()}`}
        </div>
      ) : null}
    </>
  );
};

const StudentsTableContainer = ({ students, state, studentCount, isLoading, loadError }) => {
  const { t } = useTranslation();
  const isPendingTab = state?.activeTab === 'pending';
  const currentOrganization = useOrganization();
  const { gotoRouteByName } = useRoutes();
  const isAddFamiliesEnabled = useAddFamiliesEnabled();
  const viewState = getStudentsViewState({
    isLoading,
    hasError: !!loadError,
    studentCount,
    visibleStudentCount: students?.length ?? 0,
  });

  const getAgeText = useCallback(
    (birthday) => {
      const birthdayDate = toDateObject(birthday);
      const age = getAgeFromBirthday(birthday);
      if (!age) return '';

      // Display "0" for future birthdays
      if (birthdayDate && birthdayDate.getTime() > Date.now()) {
        return '0';
      }

      // this covers babies with an assigned birthday that are less than 1 month old
      if (age.years === 0 && age.months === 0 && birthday) {
        return `< 1 ${t('students.roster.ageMonthsAbbreviation')}`;
      }
      const yearSuffix =
        age.years && age.years > 1
          ? t('students.roster.ageYearsAbbreviation')
          : t('students.roster.ageYearAbbreviation');
      const monthSuffix = t('students.roster.ageMonthsAbbreviation');
      const { months, years } = age;
      // x months && 0 years
      if (months && !years) {
        return `${months} ${monthSuffix}`;
      }
      // x years && 0 months
      if (years && !months) {
        return `${years} ${yearSuffix}`;
      }
      // everything else
      return `${years} ${yearSuffix} ${months} ${monthSuffix}`;
    },
    [t]
  );

  const { isOrganizationAdmin } = useUser();
  // we need both staff and family contacts to be able to sort by contacts
  useOrganizationContactsListener(
    currentOrganization.id,
    isOrganizationAdmin
      ? []
      : [
          {
            field: 'type',
            operation: '==',
            value: 'family',
          },
        ],
    [isOrganizationAdmin]
  );

  const rooms = useRooms();
  const dispatch = useDispatch();

  const dispatchStudentSelected = (studentId) => {
    const student = students.find((student) => student.id === studentId);

    dispatch(studentSelected(student));

    gotoRouteByName(RouteNameEnum.STUDENT_DETAILS, [{ name: 'studentId', value: studentId }]);
  };

  const [shouldOpenPdfModal, setShouldOpenPdfModal] = useState(false);

  const localeCompareSortHelper = (a, b, order) => {
    if (!b) {
      return -1;
    }
    if (!a) {
      return 1;
    }
    return order === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
  };

  const studentsPrimitive = JSON.stringify(students);
  const roomsPrimitive = JSON.stringify(rooms);

  const csvColumns = useMemo(
    () => [
      {
        key: 'displayName',
        header: t('common.name'),
      },
      {
        key: 'rooms',
        header: t('students.roster.roomColumn'),
      },
      {
        key: 'enrollmentStatus',
        header: t('common.status'),
      },
      ...(isPendingTab
        ? [
            {
              key: 'startDate',
              header: t('students.roster.startDateColumn'),
            },
          ]
        : []),
      {
        key: 'scheduleMonday',
        header: t('common.schedule'),
      },
      {
        key: 'scheduleTuesday',
        header: '',
      },
      {
        key: 'scheduleWednesday',
        header: '',
      },
      {
        key: 'scheduleThursday',
        header: '',
      },
      {
        key: 'scheduleFriday',
        header: '',
      },
      {
        key: 'scheduleSaturday',
        header: '',
      },
      {
        key: 'scheduleSunday',
        header: '',
      },
      {
        key: 'age',
        header: t('students.roster.ageColumn'),
      },
      {
        key: 'birthday',
        header: t('students.roster.birthdayColumn'),
      },
      {
        key: 'allergies',
        header: t('students.roster.allergiesColumn'),
      },
      {
        key: 'combinedFamily',
        header: t('students.roster.contactsColumn'),
      },
    ],
    [t, isPendingTab]
  );

  const csvData = useMemo(
    () =>
      studentsPrimitive && roomsPrimitive
        ? students.map((student) => ({
            displayName: student.displayName,
            rooms: rooms.find((room) => room.id === student.rooms?.[0])?.name || '',
            enrollmentStatus: capitalize(getEnrollmentStatus(student)),
            startDate: getEnrollmentDate(student) ? dateFormatter(getEnrollmentDate(student), 'MM/DD/YYYY') : '',
            scheduleMonday: student.schedule?.includes('M') ? 'M' : '',
            scheduleTuesday: student.schedule?.includes('T') ? 'T' : '',
            scheduleWednesday: student.schedule?.includes('W') ? 'W' : '',
            scheduleThursday: student.schedule?.includes('Th') ? 'Th' : '',
            scheduleFriday: student.schedule?.includes('F') ? 'F' : '',
            scheduleSaturday: student.schedule?.includes('S') ? 'S' : '',
            scheduleSunday: student.schedule?.includes('Su') ? 'Su' : '',
            age: getAgeText(student.birthday),
            birthday: student.birthday ? dateFormatter(student.birthday, 'MM/DD/YYYY') : '',
            allergies: student.allergies || '',
            combinedFamily: Object.values(student.combinedFamily)
              .map((contact) => (contact.displayName ? contact.displayName : formatFullName(contact, true)))
              .join(', '),
          }))
        : [],
    [studentsPrimitive, roomsPrimitive, rooms, students, getAgeText]
  );

  const tableColumns = useMemo(
    () => [
      {
        fieldName: 'picture',
        renderCell: (student) =>
          student.picture ? (
            <Avatar round size={44} src={student.picture} color="#346AF4" />
          ) : (
            <Avatar round size={44} value={getInitials(student.displayName || '*')} color="#346AF4" />
          ),
      },
      {
        fieldName: 'displayName',
        label: t('common.name'),
        sortBy: 'CLIENT',
        sortFunction: (a, b, order) => {
          return localeCompareSortHelper(a.displayName, b.displayName, order);
        },
        renderCell: (student) => {
          return !isStudentArchived(student) ? (
            student.displayName
          ) : (
            <div>
              <div>{student.displayName}</div>
              <div className={styles.studentArchived}>{t('students.roster.archivedStatus')}</div>
            </div>
          );
        },
      },
      {
        fieldName: 'rooms',
        label: t('students.roster.roomColumn'),
        sortBy: 'CLIENT',
        sortFunction: (a, b, order) => {
          const roomA = rooms?.find((room) => a?.rooms.indexOf(room.id) > -1);
          const roomB = rooms?.find((room) => b?.rooms.indexOf(room.id) > -1);
          return localeCompareSortHelper(roomA?.name, roomB?.name, order);
        },
        renderCell: (student) => rooms.find((room) => room.id === student.rooms?.[0])?.name || '',
      },
      {
        fieldName: 'enrollmentStatus',
        label: t('common.status'),
        renderCell: (student) => {
          const status = getEnrollmentStatus(student);
          return <EnrollmentStatusPill enrollmentStatus={status} />;
        },
      },
      ...(isPendingTab
        ? [
            {
              fieldName: 'startDate',
              label: t('students.roster.startDateColumn'),
              sortBy: 'CLIENT',
              sortFunction: (a, b) => {
                // DataTable runs Array.sort(sortFunction) with no order argument and
                // reverses the result for DESC, so this must be a plain ascending
                // comparator. Missing dates sort last ascending.
                const dateA = toDateObject(getEnrollmentDate(a))?.valueOf() ?? Number.MAX_SAFE_INTEGER;
                const dateB = toDateObject(getEnrollmentDate(b))?.valueOf() ?? Number.MAX_SAFE_INTEGER;
                return dateA - dateB;
              },
              renderCell: (student) => {
                const startDate = getEnrollmentDate(student);
                if (!startDate) return <div className={styles.studentDataPlaceholder}>-</div>;

                const formatted = dateFormatter(startDate, 'MM/DD/YYYY');
                // Derive the hint from the same UTC-anchored calendar day dateFormatter
                // displays, parsed as a local date, so the date and hint can never
                // disagree for providers behind UTC.
                const days = dayjs(dateFormatter(startDate, 'YYYY-MM-DD')).diff(dayjs().startOf('day'), 'day');

                let hint = '';
                if (days === 0) hint = t('students.startsToday');
                else if (days === 1) hint = t('students.startsTomorrow');
                else if (days > 1) hint = t('students.startsInDays', { days });
                else if (days === -1) hint = t('students.startedYesterday');
                else hint = t('students.startedDaysAgo', { days: Math.abs(days) });

                return hint ? `${formatted} · ${hint}` : formatted;
              },
            },
          ]
        : []),
      {
        fieldName: 'schedule',
        label: t('common.schedule'),
        renderCell: (student) => {
          return (
            <div>
              {getScheduledDaysAbbreviated(student)?.join('') || (
                <span className={styles.studentDataPlaceholder}>-</span>
              )}
            </div>
          );
        },
      },
      {
        fieldName: 'birthday',
        label: t('students.roster.ageColumn'),
        sortBy: 'CLIENT',
        sortFunction: (a, b) => {
          const dateA = toDateObject(a.birthday)?.valueOf();
          const dateB = toDateObject(b.birthday)?.valueOf();

          if (!dateA && !dateB) return 0;
          if (!dateB) return 1;
          if (!dateA) return -1;

          return dateB - dateA;
        },
        renderCell: (student) => {
          // returns null or object of { years: x, months: x }
          const birthday = toDateObject(student.birthday);
          const formattedBirthday = birthday ? ` (${dateFormatter(student.birthday, 'MM/DD/YYYY')})` : '';
          return `${getAgeText(birthday)}${formattedBirthday}`;
        },
      },
      {
        fieldName: 'allergies',
        label: t('students.roster.allergiesColumn'),
        renderCell: (student) => {
          return student.allergies ? student.allergies : <div className={styles.studentDataPlaceholder}>-</div>;
        },
      },
      {
        fieldName: 'combinedFamily',
        label: t('students.roster.contactsColumn'),
        renderCell: (student) => {
          if (!student.combinedFamily || !Object.keys(student)?.length) {
            return <div className={styles.studentDataPlaceholder}>-</div>;
          }
          return Object.values(student.combinedFamily).map((contact, i) => (
            <div key={i} className={styles.studentContactCellDiv}>
              {contact?.displayName ? contact?.displayName : formatFullName(contact, true)}
            </div>
          ));
        },
      },
    ],
    [rooms, getAgeText, t, isPendingTab]
  );

  //PDF Generation

  const calculateAge = (timestamp) => {
    // returns null or object of { years: x, months: x }
    const birthday = toDateObject(timestamp);
    const age = getAgeFromBirthday(birthday);
    if (!age) return '';

    // Display "0" for future birthdays
    if (birthday && birthday.getTime() > Date.now()) {
      return '0';
    }

    // this covers babies with an assigned birthday that are less than 1 month old
    if (age.years === 0 && age.months === 0 && timestamp) {
      return `< 1 ${t('students.roster.ageMonthsAbbreviation')}`;
    }
    const yearSuffix =
      age.years && age.years > 1 ? t('students.roster.ageYearsAbbreviation') : t('students.roster.ageYearAbbreviation');
    const monthSuffix = t('students.roster.ageMonthsAbbreviation');
    const { months, years } = age;
    // x months && 0 years
    if (months && !years) {
      return `${months} ${monthSuffix}`;
    }
    // x years && 0 months
    if (years && !months) {
      return `${years} ${yearSuffix}`;
    }
    // everything else
    return `${years} ${yearSuffix} ${months} ${monthSuffix}`;
  };

  const formatData = async (datas) => {
    const formattedData = [];
    for (const data of datas) {
      const doc = [
        data?.displayName,
        rooms.find((room) => room.id === data?.rooms[0])?.name ?? '',
        t(`students.enrollmentStatus.${getEnrollmentStatus(data)}`),
        ...(isPendingTab ? [getEnrollmentDate(data) ? dateFormatter(getEnrollmentDate(data), 'MM/DD/YYYY') : ''] : []),
        data?.schedule?.includes('M') ? 'M' : '',
        data?.schedule?.includes('T') ? 'T' : '',
        data?.schedule?.includes('W') ? 'W' : '',
        data?.schedule?.includes('Th') ? 'Th' : '',
        data?.schedule?.includes('F') ? 'F' : '',
        data?.schedule?.includes('S') ? 'S' : '',
        data?.schedule?.includes('Su') ? 'Su' : '',
        data?.birthday ? calculateAge(data.birthday) : '',
        data?.birthday ? `${dateFormatter(data.birthday, 'MM/DD/YYYY')}` : '',
        data?.allergies ? data.allergies : '',
        Object.values(data.combinedFamily).length > 0
          ? Object.values(data.combinedFamily).map((contact) =>
              contact?.displayName ? contact?.displayName : formatFullName(contact, true)
            )
          : '',
      ];
      formattedData.push(doc);
    }
    return formattedData;
  };

  const exportPdf = async () => {
    setShouldOpenPdfModal(true);
    const FILENAME = t('students.roster.export.pdfFileName');
    const TITLE = t('students.roster.export.title');

    // Landscape export, 2×4 inches
    const doc = new jsPDF({
      orientation: 'landscape',
    });

    const PDF_COLUMNS_HEADER = [
      { content: t('common.name'), dataKey: 'displayName' },
      { content: t('students.roster.roomColumn'), dataKey: 'rooms' },
      { content: t('common.status'), dataKey: 'enrollmentStatus' },
      ...(isPendingTab ? [{ content: t('students.roster.startDateColumn'), dataKey: 'startDate' }] : []),
      { content: t('common.schedule'), dataKey: 'schedule', colSpan: 7, styles: { halign: 'center' } },
      { content: t('students.roster.ageColumn'), dataKey: 'age' },
      { content: t('students.roster.birthdayColumn'), dataKey: 'dob' },
      { content: t('students.roster.allergiesColumn'), dataKey: 'allergies' },
      { content: t('students.roster.contactsColumn'), dataKey: 'combinedFamily' },
    ];

    const BODY_DATA = await formatData(students);

    doc.addImage(wsLogo, 'PNG', 10, 20, 40, 5);
    doc.setFontSize(14);
    doc.setFont(undefined, 'normal', 'bold');
    doc.text(TITLE, 10, 36);
    autoTable(doc, {
      columnStyles: {
        1: { cellWidth: 20 },
      },
      head: [PDF_COLUMNS_HEADER],
      body: BODY_DATA,
      startY: 45,
      headStyles: { fillColor: [41, 128, 185] },
      alternateRowStyles: { fillColor: [245] },
      theme: 'grid',
    });

    setShouldOpenPdfModal(false);
    doc.setProperties({
      title: FILENAME,
    });
    doc.autoPrint();
    doc.output('dataurlnewwindow', { FILENAME });
    doc.save(FILENAME);
  };

  return (
    <div data-testid="students-table">
      <Grid stackable>
        {/* Flag off: always show the records-count/report toolbar row (today's
            behavior). Flag on: hide it on the empty state. */}
        {(!isAddFamiliesEnabled || studentCount > 0) && (
          <Grid.Row className="no-bottom-padding ml-0">
            <Grid.Column computer={8} mobile={16} tablet={16} textAlign="left">
              <ShowRecordsCount state={state} />
            </Grid.Column>
            <Grid.Column computer={8} mobile={16} tablet={16} textAlign="right">
              <ReportToolbar
                onExportPDF={exportPdf}
                onExportCSV={() => {
                  exportDataTable(
                    csvData,
                    {
                      header: true,
                      columns: csvColumns,
                    },
                    t('students.roster.export.csvFileName', { date: dayjs().format('MM-DD-YYYY') })
                  );
                }}
              />
            </Grid.Column>
          </Grid.Row>
        )}
        <Grid.Column computer={16} mobile={16} tablet={16}>
          {viewState === 'loading' && (
            <div className="flex min-h-64 items-center justify-center" data-testid="students-loading">
              <Spinner size={WidgetSizeEnum.LARGE} />
            </div>
          )}
          {viewState === 'error' && (
            <NoResults
              title={t('studentList.loadErrorTitle')}
              subTitle={t('studentList.loadErrorSubTitle')}
              data-testid="students-load-error"
              extraContainerClasses="text-center"
            />
          )}
          {viewState === 'empty' &&
            (isAddFamiliesEnabled ? (
              <NoStudentsEmptyState />
            ) : (
              <NoResults
                title={t('studentList.emptyTitle')}
                subTitle={t('studentList.emptySubTitle')}
                data-testid="no-students"
                extraContainerClasses="text-center"
              />
            ))}
          {viewState === 'filtered-empty' && (
            <NoResults
              title={t('studentList.emptyFilterTitle')}
              subTitle={t('studentList.emptyFilterSubTitle')}
              data-testid="no-students"
              extraContainerClasses="text-center"
            />
          )}
          {viewState === 'ready' && (
            <DataTable
              hasDataCountTitle={false}
              data={students}
              columns={tableColumns}
              onClickRow={(row) => dispatchStudentSelected(row.id)}
            />
          )}
        </Grid.Column>
      </Grid>
      <Modal
        onClose={() => setShouldOpenPdfModal(false)}
        open={shouldOpenPdfModal}
        closeIcon
        size="small"
        closeOnDimmerClick={false}
        data-testid="pdf-modal"
      >
        <Modal.Content className="pdf-message-modal">
          <Header size="large" data-testid="print-pdf-modal-title">
            {t('students.roster.printAction')}
          </Header>
          <p>{t('students.roster.generatingReport')}</p>
        </Modal.Content>
      </Modal>
    </div>
  );
};

const StudentAddedBannerWrapper = ({ setState }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const newStudentAdded = useAddedStudent();

  if (!newStudentAdded?.id) return null;

  const { displayName } = newStudentAdded;

  const dispatchSelectedStudent = () => {
    dispatch({ type: 'STUDENT_SELECTED', student: newStudentAdded });
    setState((prev) => ({ ...prev, isEditOpen: true }));
  };

  const onCloseBanner = () => {
    dispatch(studentAddCleared());
  };

  return (
    <Segment basic clearing className={styles.studentAddedBannerWrap}>
      <ShowSuccess
        onDismiss={onCloseBanner}
        hideHeader
        content={
          <div>
            <Trans i18nKey="students.roster.addedBanner" displayName={displayName}>
              You have added <strong>{{ displayName }}</strong> as a new student.
            </Trans>
            &nbsp;
            <span className="pseudo-link" onClick={dispatchSelectedStudent}>
              {t('students.roster.viewStudentAction')}
            </span>
          </div>
        }
      />
    </Segment>
  );
};
const StudentsTable = ({ filter }) => {
  const enrollmentStatus =
    filter === 'all' ? '' : ['enrolled', 'unenrolled', 'pending'].includes(filter) ? filter : 'enrolled';

  const [state, setState] = useState({
    activeTab: ['all', 'enrolled', 'unenrolled', 'pending'].includes(filter) ? filter : 'enrolled',
    isAddOpen: false,
    isEditOpen: false,
    searchText: '',
    filters: {
      room: '',
      enrollmentStatus: enrollmentStatus,
      age: '',
      schedule: [],
      allergies: '',
      enrollmentSource: '',
      hideArchivedStudents: true,
      programs: '',
    },
    newStudentId: null,
    count: {
      all: 0,
      enrolled: 0,
      unenrolled: 0,
      pending: 0,
    },
  });
  const { listFiltered, count, isLoading, error } = useStudents(state);
  const isAddFamiliesEnabled = useAddFamiliesEnabled();

  return (
    <MainContentLayout>
      <PageTitle state={state} setState={setState} />
      <StudentAddedBannerWrapper setState={setState} />
      {/* Flag off: always show filters (today's behavior). Flag on: hide them
          on the empty state so the blank-state card stands alone. */}
      {(!isAddFamiliesEnabled || count > 0) && (
        <div className="mb-4 py-4">
          <Grid columns={1} stackable>
            <Grid.Column tablet={16} mobile={16} computer={16}>
              <StudentsFilters state={state} setState={setState} />
            </Grid.Column>
          </Grid>
        </div>
      )}
      <StudentsTableContainer
        state={state}
        students={listFiltered}
        studentCount={count}
        isLoading={isLoading}
        loadError={error}
        setState={setState}
      />
    </MainContentLayout>
  );
};

export default StudentsTable;
